/**
 * The office's chase list: gear that is OUT and owes somebody an answer.
 *
 * ─── WHY THIS EXISTS ────────────────────────────────────────────────────────
 *
 * `GET /dispatch/equipment-alerts` was built for exactly this and shipped with
 * ZERO frontend callers — nothing in `src/` referenced it outside the generated
 * api-manifest. Meanwhile `AlertsScreen` answered the same question by pulling
 * the whole equipment table and filtering `status === 'left_on_site'` in the
 * browser, which can see the status and nothing else: not how long the gear has
 * been sitting, not who last touched it, and not the `finished_job` class at
 * all.
 *
 * ─── TWO CLASSES, ONE PROBLEM, RENDERED DISTINCTLY ──────────────────────────
 *
 * To dispatch these are the same problem — gear that is out and nobody has
 * declared what happens next — so they belong on one list. They are NOT the
 * same fact, so they keep their labels:
 *
 *   `left_on_site`   a POSITIVE CLAIM. A hand said the gear stays on the lease.
 *                    Someone decided this; it is deliberate placement that has
 *                    not been closed out.
 *   `finished_job`   the ABSENCE of a claim. The job ended and the asset is
 *                    still `in_use` — nobody said where the tong went.
 *
 * Folding the two would lose the distinction that makes one of them actionable
 * in a different way, which is why the API keeps them apart and why the
 * `isOnFinishedJob` mirror in equipmentDisposition.ts refuses to include
 * `left_on_site`.
 *
 * ─── `maintenance` IS DELIBERATELY NOT HERE ─────────────────────────────────
 *
 * The endpoint returns a third class. Gear in the shop is gear somebody HAS
 * declared the next step for — it is accounted for, not adrift — so it is not
 * part of "nobody has said what happens next" and putting it on a chase list
 * would dilute the two classes that are. It stays visible on the equipment
 * screen's own Maintenance filter.
 *
 * ─── THE AGE IS THE NUMBER THAT MATTERS, AND IT MAY BE UNKNOWN ──────────────
 *
 * `days_since_job_closed` comes off `jobs.completed_at` and the API does NOT
 * coalesce it: 87 completed jobs estate-wide carry a NULL `completed_at`
 * (they predate `jobStatusTimestampSet`). An unknown age rendered as `0 days`
 * reads as "closed today" — the exact inversion the absent-renders-as-— rule
 * exists to prevent — so it renders as `—` here and sorts FIRST, because an
 * unknown age is not a small age.
 */

/**
 * One row of `GET /dispatch/equipment-alerts`.
 *
 * snake_case, and that is not drift: the handler returns raw pg rows. Typed to
 * the payload as it actually is rather than to the convention, per the
 * field-name-mismatch rule — an interface that declares the casing we wish the
 * endpoint used is the bug, not the fix.
 */
export interface EquipmentAlertRow {
  id: string;
  name: string | null;
  type: string | null;
  serial_number: string | null;
  status: string;
  current_job_id: string | null;
  last_known_rig: string | null;
  last_known_well: string | null;
  job_number: string | null;
  /** The rig of the job the asset is pointed at. API #634. */
  job_rig_name: string | null;
  job_status: string | null;
  job_completed_at: string | null;
  alert_class: 'left_on_site' | 'finished_job' | 'maintenance';
  days_since_job_closed: number | null;
  last_check: {
    user_name?: string | null;
    left_on_site_reason?: string | null;
    notes?: string | null;
    created_at?: string | null;
  } | null;
}

/** The two classes this screen chases, in the order they are shown. */
export const CHASEABLE_ALERT_CLASSES = ['finished_job', 'left_on_site'] as const;
export type ChaseableAlertClass = (typeof CHASEABLE_ALERT_CLASSES)[number];

export const ALERT_CLASS_LABEL: Record<ChaseableAlertClass, string> = {
  finished_job: 'Job Finished — No Disposition',
  left_on_site: 'Left On Site',
};

/**
 * What each class MEANS, in a dispatcher's words. Rendered under the heading so
 * the two are not read as one list with a colour on it.
 */
export const ALERT_CLASS_BLURB: Record<ChaseableAlertClass, string> = {
  finished_job: 'The job ended and nobody said where this went.',
  left_on_site: 'A hand declared it stays on the lease.',
};

export function isChaseable(r: EquipmentAlertRow): boolean {
  return (CHASEABLE_ALERT_CLASSES as readonly string[]).includes(r.alert_class);
}

/**
 * Rows of one class, oldest first, with UNKNOWN ages leading.
 *
 * `null` sorts before every number on purpose. Burying the rows nobody can date
 * beneath a known 28-day one hides exactly the assets with the least evidence
 * attached to them.
 */
export function alertsOfClass(
  rows: EquipmentAlertRow[] | undefined,
  cls: ChaseableAlertClass
): EquipmentAlertRow[] {
  return (rows ?? [])
    .filter((r) => r.alert_class === cls)
    .sort((a, b) => {
      const av = a.days_since_job_closed;
      const bv = b.days_since_job_closed;
      if (av === null || av === undefined) return bv === null || bv === undefined ? 0 : -1;
      if (bv === null || bv === undefined) return 1;
      return bv - av;
    });
}

/** How many rows this screen is responsible for. Excludes `maintenance`. */
export function chaseableCount(rows: EquipmentAlertRow[] | undefined): number {
  return (rows ?? []).filter(isChaseable).length;
}

/**
 * The age, or `—`.
 *
 * NEVER `0 days` for an absent value. `0` is a real and different answer — the
 * job closed today — and the two must not render alike.
 */
export function ageLabel(days: number | null | undefined): string {
  if (days === null || days === undefined) return '—';
  if (days === 0) return 'closed today';
  return `${days} day${days === 1 ? '' : 's'}`;
}

/**
 * Where the asset is: its own last known rig, then the rig of the job it is
 * pointed at.
 *
 * Two different facts, and NEITHER ALONE COVERS THE POPULATION. Measured on
 * production 2026-09-09 over all 47 chaseable rows: `last_known_rig` locates 9,
 * `job_rig_name` locates 47, neither locates 0. The asset's own column is
 * preferred where present because it is the more recent claim — a hand said it,
 * about this asset — and the job's rig is where it falls back.
 *
 * The `finished_job` class is why the fallback carries the weight: an asset goes
 * `in_use` without anybody writing `last_known_rig`, so 37 of 41 such rows have
 * no rig of their own.
 */
export function alertLocation(r: EquipmentAlertRow): string | null {
  return r.last_known_rig || r.job_rig_name || null;
}

/** Identity plus location plus the job that left it, absent parts omitted. */
export function describeAlertRow(r: EquipmentAlertRow): string {
  const parts: string[] = [];
  if (r.serial_number) parts.push(`Unit ${r.serial_number}`);
  else if (r.type) parts.push(r.type);
  const rig = alertLocation(r);
  if (rig) parts.push(rig);
  if (r.job_number) parts.push(r.job_number);
  return parts.length > 0 ? parts.join(' · ') : 'Equipment';
}
